import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Table } from 'reactstrap';
import { JhiItemCount, JhiPagination, Translate, getPaginationState } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './country-of-birth.reducer';

export const CountryOfBirth = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const countryOfBirthList = useAppSelector(state => state.countryOfBirth.entities);
  const loading = useAppSelector(state => state.countryOfBirth.loading);
  const totalItems = useAppSelector(state => state.countryOfBirth.totalItems);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
      }),
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="country-of-birth-heading" data-cy="CountryOfBirthHeading">
        <Translate contentKey="apiLocationApp.countryOfBirth.home.title">Country Of Births</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="apiLocationApp.countryOfBirth.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link to="/country-of-birth/new" className="btn btn-primary jh-create-entity" id="jh-create-entity" data-cy="entityCreateButton">
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="apiLocationApp.countryOfBirth.home.createLabel">Create new Country Of Birth</Translate>
          </Link>
        </div>
      </h2>
      <div className="table-responsive">
        {countryOfBirthList && countryOfBirthList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="apiLocationApp.countryOfBirth.id">ID</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('geographicLevel')}>
                  <Translate contentKey="apiLocationApp.countryOfBirth.geographicLevel">Geographic Level</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('geographicLevel')} />
                </th>
                <th className="hand" onClick={sort('date')}>
                  <Translate contentKey="apiLocationApp.countryOfBirth.date">Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('date')} />
                </th>
                <th className="hand" onClick={sort('geography')}>
                  <Translate contentKey="apiLocationApp.countryOfBirth.geography">Geography</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('geography')} />
                </th>
                <th className="hand" onClick={sort('geographyCode')}>
                  <Translate contentKey="apiLocationApp.countryOfBirth.geographyCode">Geography Code</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('geographyCode')} />
                </th>
                <th className="hand" onClick={sort('totalAllUsualResidents')}>
                  <Translate contentKey="apiLocationApp.countryOfBirth.totalAllUsualResidents">Total All Usual Residents</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('totalAllUsualResidents')} />
                </th>
                <th className="hand" onClick={sort('ukTotal')}>
                  <Translate contentKey="apiLocationApp.countryOfBirth.ukTotal">Uk Total</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('ukTotal')} />
                </th>
                <th className="hand" onClick={sort('england')}>
                  <Translate contentKey="apiLocationApp.countryOfBirth.england">England</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('england')} />
                </th>
                <th className="hand" onClick={sort('northernIreland')}>
                  <Translate contentKey="apiLocationApp.countryOfBirth.northernIreland">Northern Ireland</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('northernIreland')} />
                </th>
                <th className="hand" onClick={sort('scotland')}>
                  <Translate contentKey="apiLocationApp.countryOfBirth.scotland">Scotland</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('scotland')} />
                </th>
                <th className="hand" onClick={sort('wales')}>
                  <Translate contentKey="apiLocationApp.countryOfBirth.wales">Wales</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('wales')} />
                </th>
                <th className="hand" onClick={sort('republicOfIreland')}>
                  <Translate contentKey="apiLocationApp.countryOfBirth.republicOfIreland">Republic Of Ireland</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('republicOfIreland')} />
                </th>
                <th className="hand" onClick={sort('otherEuCountries')}>
                  <Translate contentKey="apiLocationApp.countryOfBirth.otherEuCountries">Other Eu Countries</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('otherEuCountries')} />
                </th>
                <th className="hand" onClick={sort('africa')}>
                  <Translate contentKey="apiLocationApp.countryOfBirth.africa">Africa</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('africa')} />
                </th>
                <th className="hand" onClick={sort('middleEastAsia')}>
                  <Translate contentKey="apiLocationApp.countryOfBirth.middleEastAsia">Middle East Asia</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('middleEastAsia')} />
                </th>
                <th className="hand" onClick={sort('farEast')}>
                  <Translate contentKey="apiLocationApp.countryOfBirth.farEast">Far East</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('farEast')} />
                </th>
                <th className="hand" onClick={sort('theAmericas')}>
                  <Translate contentKey="apiLocationApp.countryOfBirth.theAmericas">The Americas</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('theAmericas')} />
                </th>
                <th className="hand" onClick={sort('antarcticaOceania')}>
                  <Translate contentKey="apiLocationApp.countryOfBirth.antarcticaOceania">Antarctica Oceania</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('antarcticaOceania')} />
                </th>
                <th className="hand" onClick={sort('otherCountries')}>
                  <Translate contentKey="apiLocationApp.countryOfBirth.otherCountries">Other Countries</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('otherCountries')} />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {countryOfBirthList.map((countryOfBirth, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/country-of-birth/${countryOfBirth.id}`} color="link" size="sm">
                      {countryOfBirth.id}
                    </Button>
                  </td>
                  <td>
                    <Translate contentKey={`apiLocationApp.GeographicLevel.${countryOfBirth.geographicLevel}`} />
                  </td>
                  <td>{countryOfBirth.date}</td>
                  <td>{countryOfBirth.geography}</td>
                  <td>{countryOfBirth.geographyCode}</td>
                  <td>{countryOfBirth.totalAllUsualResidents}</td>
                  <td>{countryOfBirth.ukTotal}</td>
                  <td>{countryOfBirth.england}</td>
                  <td>{countryOfBirth.northernIreland}</td>
                  <td>{countryOfBirth.scotland}</td>
                  <td>{countryOfBirth.wales}</td>
                  <td>{countryOfBirth.republicOfIreland}</td>
                  <td>{countryOfBirth.otherEuCountries}</td>
                  <td>{countryOfBirth.africa}</td>
                  <td>{countryOfBirth.middleEastAsia}</td>
                  <td>{countryOfBirth.farEast}</td>
                  <td>{countryOfBirth.theAmericas}</td>
                  <td>{countryOfBirth.antarcticaOceania}</td>
                  <td>{countryOfBirth.otherCountries}</td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button tag={Link} to={`/country-of-birth/${countryOfBirth.id}`} color="info" size="sm" data-cy="entityDetailsButton">
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/country-of-birth/${countryOfBirth.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() =>
                          (window.location.href = `/country-of-birth/${countryOfBirth.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                        }
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="apiLocationApp.countryOfBirth.home.notFound">No Country Of Births found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={countryOfBirthList && countryOfBirthList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default CountryOfBirth;
